import React, { useEffect, useState, memo, useMemo } from 'react';
import { YStack, XStack } from 'tamagui';
import CobuCard from '../layout/CobuCard';

const SHIMMER_INTERVAL = 1000;
const GRID_ANIMATION_INTERVAL = 2000;
const LEFT_COLUMN_HEIGHTS = ["$44", "$28", "$36"];
const RIGHT_COLUMN_HEIGHTS = ["$20", "$40", "$24"];
const LEFT_COLUMN_2_HEIGHTS = ["$36", "$32"];
const RIGHT_COLUMN_2_HEIGHTS = ["$24", "$44"];



interface CobuSkeletonPlaceholderProps {
    children?: React.ReactNode;
}

interface SkeletonBoxProps {
    width?: number | string;
    height?: string;
    borderRadius?: string;
    marginBottom?: string;
    shimmer?: boolean;
}

const SkeletonBox = memo<SkeletonBoxProps>(({
    width = "100%",
    height = "$5",
    borderRadius = "$2",
    marginBottom = "$0",
    shimmer = false,
}) => {
    const [isShimmering, setIsShimmering] = useState(false);

    useEffect(() => {
        if (!shimmer) return;

        const interval = setInterval(() => {
            setIsShimmering(prev => !prev);
        }, SHIMMER_INTERVAL);

        return () => clearInterval(interval);
    }, [shimmer]);

    return (
        <YStack
            w={width as any}
            h={height as any}
            br={borderRadius as any}
            mb={marginBottom as any}
            bg="$colorSecondary"
            animation="slow"
            animateOnly={shimmer ? ['opacity'] : []}
            o={shimmer ? (isShimmering ? 0.7 : 0.3) : 0.7}
        />
    );
});

SkeletonBox.displayName = 'SkeletonBox';

const SkeletonColumn = memo<{
    heights: string[];
    keyPrefix: string;
}>(({ heights, keyPrefix }) => (
    <YStack f={1} gap="$2">
        {heights.map((height, index) => (
            <YStack
                key={`${keyPrefix}-${index}`}
                pt="$3"
                pb="$2"
                pl="$1"
                pr="$2"
                ml="$1"
                mr="$1"
                animation="fast"
                enterStyle={{ o: 0, scale: 0.95 }}
                animateOnly={['opacity', 'scale']}
            >
                <SkeletonBox
                    height={height}
                    width="100%"
                    borderRadius="$3"
                    shimmer
                />
            </YStack>
        ))}
    </YStack>
));

SkeletonColumn.displayName = 'SkeletonColumn';

const CobuSkeletonPlaceholder: React.FC<CobuSkeletonPlaceholderProps> = ({
    children,
}) => {
    return <YStack>{children}</YStack>;
};

export const GroupDetailSkeleton = memo(() => (
    <YStack p="$4" gap="$4">
        <YStack gap="$2">
            <SkeletonBox height="$5" width="$20" marginBottom="$1" shimmer />
            <SkeletonBox height="$5" width="$24" marginBottom="$1" shimmer />
            <SkeletonBox height="$16" width="$40" marginBottom="$1" shimmer />
        </YStack>

        <YStack gap="$2">
            <SkeletonBox height="$5" width="$24" marginBottom="$1" shimmer />
            <SkeletonBox height="$4" width="80%" marginBottom="$1" shimmer />
            <SkeletonBox height="$4" width="60%" marginBottom="$1" shimmer />
            <SkeletonBox height="$4" width="70%" marginBottom="$2" shimmer />
        </YStack>

        <YStack gap="$2">
            <SkeletonBox height="$5" width="$20" marginBottom="$1" shimmer />
            <SkeletonBox height="$14" width="100%" marginBottom="$1" shimmer />
        </YStack>
    </YStack>
));

GroupDetailSkeleton.displayName = 'GroupDetailSkeleton';

export const GroupGatheringSkeleton = memo(() => (
    <XStack gap="$3" p="$4">
        {Array.from({ length: 2 }).map((_, index) => (
            <YStack key={index} w="$40" gap="$2">
                <SkeletonBox height="$20" width="$40" marginBottom="$1" shimmer />
                <SkeletonBox height="$4" width="$24" marginBottom="$1" shimmer />
                <SkeletonBox height="$3" width="$20" shimmer />
            </YStack>
        ))}
    </XStack>
));

GroupGatheringSkeleton.displayName = 'GroupGatheringSkeleton';

export const GroupsGridSkeleton = memo(() => {
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationKey(prev => prev + 1);
        }, GRID_ANIMATION_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    const memoizedContent = useMemo(() => (
        <>
            <YStack px="$4" pt="$4">
                <SkeletonBox height="$6" width="$24" shimmer />
            </YStack>

            <YStack px="$2">
                <XStack gap="$2" jc="space-between">
                    <SkeletonColumn heights={LEFT_COLUMN_HEIGHTS} keyPrefix="left" />
                    <SkeletonColumn heights={RIGHT_COLUMN_HEIGHTS} keyPrefix="right" />
                </XStack>
            </YStack>

            <YStack px="$4" pt="$4">
                <SkeletonBox height="$6" width="$28" shimmer />
            </YStack>

            <YStack px="$2">
                <XStack gap="$2" jc="space-between">
                    <SkeletonColumn heights={LEFT_COLUMN_2_HEIGHTS} keyPrefix="left2" />
                    <SkeletonColumn heights={RIGHT_COLUMN_2_HEIGHTS} keyPrefix="right2" />
                </XStack>
            </YStack>
        </>
    ), []);

    return (
        <YStack gap="$3" mb="$4" key={animationKey}>
            {memoizedContent}
        </YStack>
    );
});

GroupsGridSkeleton.displayName = 'GroupsGridSkeleton';

// New: ProfileSkeleton for Public Profile screen shimmer UI
export const ProfileSkeleton = memo(() => (
    <YStack>
        {/* Header background */}
        <YStack h={140} bc="$cobuDarkBlue" />

        {/* Content */}
        <YStack mt={-12} px="$0">
            {/* Main Profile Card */}
            <CobuCard bc="$white" mb="$2" p="$0">
                {/* General Info Section */}
                <XStack ai="center" jc="space-between" px="$4" mt={-12}>
                    <XStack ai="center" f={1} gap="$3">
                        {/* Avatar placeholder (80x80) */}
                        <YStack w={80} h={80} br={40} bc="$colorSecondary" o={0.6} />
                        {/* Name + Building */}
                        <YStack f={1} gap="$2">
                            <SkeletonBox height="$5" width="$28" shimmer />
                            <SkeletonBox height="$4" width="$32" shimmer />
                        </YStack>
                    </XStack>
                    {/* More icon placeholder (24) */}
                    <YStack w={24} h={24} br="$3" bc="$colorSecondary" o={0.6} mr="$4" />
                </XStack>

                {/* Actions / Membership chip (message + chip) */}
                <YStack px="$4" pb="$4" pt="$2">
                    <XStack mt="$4" ai="center">
                        {/* Message button placeholder */}
                        <YStack w="$36" h="$7" br={20} bc="$colorSecondary" o={0.6} />

                        {/* Membership chip placeholder */}
                        <XStack ai="center" bc="$cobuTopaz10" br={20} py="$1" px="$3" ml="$2" f={1}>
                            <YStack w={24} h={24} br={12} bc="$colorSecondary" o={0.5} />
                            <SkeletonBox height="$4" width="$40" marginBottom="$0" shimmer />
                        </XStack>
                    </XStack>
                </YStack>

                {/* About Section */}
                <YStack px="$4" pb="$4">
                    <SkeletonBox height="$6" width="$28" marginBottom="$2" shimmer />
                    <SkeletonBox height="$4" width="90%" marginBottom="$1" shimmer />
                    <SkeletonBox height="$4" width="85%" marginBottom="$1" shimmer />
                    <SkeletonBox height="$4" width="80%" shimmer />
                </YStack>
            </CobuCard>

            {/* Separator */}
            <YStack h={8} mx="$0" my="$2" bc="$backgroundSecondary" />

            {/* Skills Section */}
            <CobuCard bc="$white" mb="$2" p="$0">
                <YStack px="$4" py="$4" gap="$3">
                    <SkeletonBox height="$6" width="$24" shimmer />
                    {/* Helping hand item */}
                    <XStack ai="center" gap="$2">
                        <YStack w={16} h={16} br={8} bc="$colorSecondary" o={0.5} />
                        <SkeletonBox height="$4" width="$40" shimmer />
                    </XStack>
                    <XStack ai="center" gap="$2">
                        <YStack w={16} h={16} br={8} bc="$colorSecondary" o={0.5} />
                        <SkeletonBox height="$4" width="$36" shimmer />
                    </XStack>
                    {/* Needs help header + one item */}
                    <SkeletonBox height="$4" width="$28" shimmer />
                    <XStack ai="center" gap="$2">
                        <YStack w={16} h={16} br={8} bc="$colorSecondary" o={0.5} />
                        <SkeletonBox height="$4" width="$44" shimmer />
                    </XStack>
                </YStack>
            </CobuCard>

            {/* Separator */}
            <YStack h={8} mx="$0" my="$2" bc="$backgroundSecondary" />

            {/* Groups Section - horizontal cards skeleton */}
            <CobuCard bc="$white" mb="$2" p="$0">
                <YStack pt="$4" px="$4">
                    <SkeletonBox height="$6" width="$36" shimmer />
                </YStack>
                <XStack px="$2" pb="$4" gap="$2">
                    {Array.from({ length: 3 }).map((_, idx) => (
                        <YStack key={idx} w={132} h={156} br={10} bc="$colorSecondary" o={0.6} />
                    ))}
                </XStack>
            </CobuCard>
        </YStack>
    </YStack>
));

ProfileSkeleton.displayName = 'ProfileSkeleton';

export const EventsListSkeleton = memo(() => {
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationKey(prev => prev + 1);
        }, GRID_ANIMATION_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    const memoizedContent = useMemo(() => (
        <>
            <YStack px="$4" pt="$6">
                <SkeletonBox height="$6" width="$24" shimmer />
            </YStack>

            <YStack px="$2">
                <XStack gap="$2" jc="space-between">
                    <SkeletonColumn heights={LEFT_COLUMN_HEIGHTS} keyPrefix="left" />
                </XStack>
            </YStack>
        </>
    ), []);

    return (
        <YStack gap="$3" mb="$4" key={animationKey}>
            {memoizedContent}
        </YStack>
    );
});


export const EventDetailSkeleton = memo(() => {
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationKey(prev => prev + 1);
        }, GRID_ANIMATION_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    const memoizedContent = useMemo(() => (
        <>
            {/* Hero image placeholder */}
            <YStack pos="relative">
                <YStack h="$56" bg="$colorSecondary" o={0.5} />
                {/* Resident organized banner placeholder */}
                <YStack pos="absolute" b="$5" r="$0" px="$3" py="$2" bg="$colorSecondary" o={0.6} btlr="$4" bblr="$4" w="$28" />
            </YStack>

            {/* Content */}
            <YStack px="$4" pt="$4" gap="$3">
                {/* Title + creator menu */}
                <XStack ai="center" jc="space-between">
                    <SkeletonBox height="$7" width="70%" shimmer />
                    <YStack w={24} h={24} br="$3" bg="$colorSecondary" o={0.6} />
                </XStack>

                {/* Date/Time rows */}
                <YStack gap="$1">
                    <SkeletonBox height="$4" width="85%" shimmer />
                    <SkeletonBox height="$4" width="60%" shimmer />
                </YStack>

                {/* Calendar button (pill) */}
                <YStack pt="$2">
                    <YStack w="$44" h="$7" br={20} bg="$colorSecondary" o={0.6} />
                </YStack>

                {/* Creator info */}
                <XStack ai="center" gap="$3" py="$2">
                    <YStack w="$10" h="$10" br="$full" bg="$colorSecondary" o={0.6} />
                    <YStack f={1} gap="$1">
                        <SkeletonBox height="$4" width="40%" shimmer />
                        <SkeletonBox height="$3" width="24%" shimmer />
                    </YStack>
                </XStack>

                {/* Meta details: Location */}
                <XStack ai="flex-start" gap="$3">
                    <YStack w={16} h={16} br={8} bg="$colorSecondary" o={0.6} />
                    <YStack f={1}>
                        <SkeletonBox height="$4" width="70%" shimmer />
                    </YStack>
                </XStack>

                {/* Meta details: Interests */}
                <XStack ai="flex-start" gap="$3">
                    <YStack w={16} h={16} br={8} bg="$colorSecondary" o={0.6} />
                    <YStack f={1}>
                        <SkeletonBox height="$4" width="50%" shimmer />
                    </YStack>
                </XStack>

                {/* What to bring card */}
                <YStack my="$2" py="$3" px="$4" bg="$cobuDarkBlue10" br="$3" gap="$2">
                    <SkeletonBox height="$5" width="$28" shimmer />
                    <SkeletonBox height="$4" width="90%" shimmer />
                    <SkeletonBox height="$4" width="80%" shimmer />
                </YStack>
            </YStack>

            {/* Comments header */}
            <YStack mt="$2" px="$4" pb="$2">
                <XStack ai="center" jc="space-between">
                    <SkeletonBox height="$6" width="$24" shimmer />
                    <XStack ai="center" bc="$white" br="$6" h="$10" px="$4" bw={'$0.5'} boc="$cobuPink25">
                        <YStack w={22} h={22} br={11} bg="$colorSecondary" o={0.6} />
                        <YStack w="$2" />
                        <SkeletonBox height="$4" width="$28" shimmer />
                    </XStack>
                </XStack>
            </YStack>

            {/* Comment items */}
            <YStack px="$4" gap="$4" pb="$8">
                {Array.from({ length: 2 }).map((_, idx) => (
                    <XStack key={idx} ai="flex-start" gap="$3">
                        <YStack w={32} h={32} br={16} bg="$colorSecondary" o={0.6} />
                        <YStack f={1} gap="$2">
                            <SkeletonBox height="$4" width="50%" shimmer />
                            <SkeletonBox height="$4" width="85%" shimmer />
                        </YStack>
                    </XStack>
                ))}
            </YStack>

            {/* Footer action area (RSVP) */}
            <YStack h="$16" bc="$white" px="$4" pt="$3" pb="$4"
                style={{ borderTopWidth: 1, borderTopColor: 'rgba(0,0,0,0.06)' }}>
                <YStack ai="center">
                    <YStack w="70%" h="$8" br={24} bg="$colorSecondary" o={0.6} />
                </YStack>
            </YStack>
        </>
    ), []);

    return (
        <YStack gap="$3" mb="$4" key={animationKey}>
            {memoizedContent}
        </YStack>
    );
});


export const PerksListSkeleton = memo(() => {
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationKey(prev => prev + 1);
        }, GRID_ANIMATION_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    const memoizedContent = useMemo(() => (
        <>

            {/* List of perk cards */}
            <YStack>
                {Array.from({ length: 5 }).map((_, idx) => (
                    <YStack key={idx} mx="$3" my="$2" br="$3" bc="$white" shac="$shadowGray" shof={{ width: 0, height: 0 }} shar="$1" elevation="$1">
                        <XStack h="$20" br="$3" ov="hidden">
                            {/* Left image/gradient area */}
                            <YStack w="$20" h="100%" bg="$colorSecondary" o={0.5} />
                            {/* Right text area */}
                            <YStack f={1} p="$3" jc="center" gap="$2">
                                <SkeletonBox height="$4" width="70%" shimmer />
                                <SkeletonBox height="$3" width="40%" shimmer />
                            </YStack>
                        </XStack>
                    </YStack>
                ))}
            </YStack>
        </>
    ), []);

    return (
        <YStack gap="$3" mb="$4" key={animationKey}>
            {memoizedContent}
        </YStack>
    );
});


export const PerkDetailSkeleton = memo(() => {
    const [animationKey, setAnimationKey] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setAnimationKey(prev => prev + 1);
        }, GRID_ANIMATION_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    const memoizedContent = useMemo(() => (
        <>
            {/* Header image */}
            <YStack pos="relative">
                <YStack h="$56" bg="$colorSecondary" o={0.5} />
                {/* Saved check indicator placeholder */}
                <YStack pos="absolute" t="$4" r="$4" br="$5" p="$2" bg="$colorSecondary" o={0.6}>
                    <YStack w={24} h={24} br={12} bg="$colorSecondary" o={0.7} />
                </YStack>
            </YStack>

            {/* Perk info */}
            <YStack p="$4" gap="$3" style={{ borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.06)' }}>
                <SkeletonBox height="$7" width="80%" shimmer />
                <SkeletonBox height="$4" width="50%" shimmer />
                <XStack ai="center" gap="$2">
                    <YStack w={16} h={16} br={8} bg="$colorSecondary" o={0.6} />
                    <SkeletonBox height="$3" width="$28" shimmer />
                </XStack>
            </YStack>

            {/* Redemption count (optional) */}
            <YStack p="$4" style={{ borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.06)' }}>
                <SkeletonBox height="$3" width="$56" shimmer />
            </YStack>

            {/* Description */}
            <YStack p="$4" mb="$4" gap="$2">
                <SkeletonBox height="$4" width="90%" shimmer />
                <SkeletonBox height="$4" width="85%" shimmer />
                <SkeletonBox height="$4" width="75%" shimmer />
            </YStack>

            {/* Bottom action buttons */}
            <YStack px="$4" py="$3" style={{ borderTopWidth: 1, borderTopColor: 'rgba(0,0,0,0.06)' }}>
                <XStack gap="$2">
                    <YStack f={1} h="$8" br={24} bg="$colorSecondary" o={0.6} />
                    <YStack f={1} h="$8" br={24} bg="$colorSecondary" o={0.6} />
                    <YStack f={1} h="$8" br={24} bg="$colorSecondary" o={0.6} />
                </XStack>
            </YStack>
        </>
    ), []);

    return (
        <YStack gap="$3" mb="$4" key={animationKey}>
            {memoizedContent}
        </YStack>
    );
});

export const PlacesListSkeleton = memo(() => (
    <YStack gap="$3" p="$4">
        {Array.from({ length: 4 }).map((_, index) => (
            <YStack key={index} gap="$2" p="$4" br="$4" bg="$backgroundSecondary">
                <XStack ai="center" jc="space-between">
                    <SkeletonBox height="$5" width="60%" shimmer />
                    <SkeletonBox height="$4" width="$16" borderRadius="$full" shimmer />
                </XStack>
                <SkeletonBox height="$3" width="40%" shimmer />
                <SkeletonBox height="$4" width="90%" shimmer />
            </YStack>
        ))}
    </YStack>
));

PlacesListSkeleton.displayName = 'PlacesListSkeleton';

export default CobuSkeletonPlaceholder; 
